import React from "react";

/**
 * Utility functions for navigation component styling
 * Uses Tailwind height-based screen variants (short, medium-h, tall) defined in tailwind.config.js
 */

/**
 * Get responsive text size classes using Tailwind height breakpoints
 * short (< 600px height): text-xs
 * normal: text-sm md:text-base
 */
export function getTextSizeClasses(
  _navOverflow?: boolean,
  _screenSize?: "small" | "medium" | "large",
): string {
  return "text-sm md:text-base short:text-xs";
}

/**
 * Get responsive icon size classes using Tailwind height breakpoints
 * short (< 600px height): h-3 w-3
 * normal: h-5 w-5
 */
export function getIconSizeClasses(
  _navOverflow?: boolean,
  _screenSize?: "small" | "medium" | "large",
): string {
  return "h-5 w-5 short:h-3 short:w-3 mr-1 flex-shrink-0";
}

/**
 * Get navigation item container classes
 */
export function getNavItemClasses(
  _navOverflow?: boolean,
  _screenSize?: "small" | "medium" | "large",
): string {
  return `border-solid border-0 rounded-md m-1 hover:bg-zinc-300 dark:hover:bg-dark-background text-zinc-700 ${getTextSizeClasses()} font-primary`;
}

/**
 * Nav item container classes without hover (for dropdowns so only the trigger and sub-links highlight)
 */
export function getNavItemClassesNoHover(
    _navOverflow?: boolean,
    _screenSize?: "small" | "medium" | "large",
  ): string {
    return `border-solid border-0 rounded-md m-1 text-zinc-700 ${getTextSizeClasses()} font-primary`;
  }

/**
 * Get link styling based on expansion state and desktop mode
 * @param alwaysExpanded - Whether navigation is always expanded
 * @param isDesktop - Whether in desktop view
 * @param currentPath - Current location pathname
 * @param targetPath - Target path for the link
 * @param isRootMap - Whether this is the root map view
 * @returns Object with width style and className
 */
export function getLinkStyling(
  alwaysExpanded?: boolean,
  isDesktop: boolean = true,
  currentPath: string = "",
  targetPath: string = "",
  isRootMap?: boolean,
): { widthStyle: React.CSSProperties; className: string } {
  const isActive =
    currentPath === targetPath || (currentPath === "/" && isRootMap);

  const baseClasses =
    "text-zinc-700 dark:text-white flex items-center justify-start p-1";

  const widthStyle: React.CSSProperties = alwaysExpanded
    ? { width: "100%" }
    : {};

  return { widthStyle, className: baseClasses };
}

/**
 * Get sidebar container classes
 * @param clientID - Client ID for theme-specific styling
 * @param navOverflow - Whether navigation is in overflow state
 * @param open - Whether mobile navigation is open
 * @param alwaysExpanded - Whether navigation is always expanded (not used in current implementation)
 * @returns Tailwind classes for sidebar container
 */
export function getSidebarClasses(
  clientID: string,
  _navOverflow: boolean,
  open: boolean,
  _alwaysExpanded?: boolean,
): string {
  const fontWeight = clientID === "default" ? "font-medium" : "font-semibold";
  // Use Tailwind height breakpoint for padding: py-2 when short, py-4 otherwise
  const padding = "py-4 short:py-2";

  return `z-[99] fixed bg-backgroundNav dark:!bg-dark-backgroundNav flex flex-col w-64 px-2 ${padding} top-12 left-0 h-[calc(100vh-3rem)] max-h-[calc(100vh-3rem)] overflow-y-auto transition-all duration-200 ease-out ${fontWeight} ${
    open ? "translate-x-0" : "-translate-x-full"
  } lg:translate-x-0 scrollbar scrollbar-thin scrollbar-thumb-rounded scrollbar-thumb-zinc-400 scrollbar-track-transparent dark:scrollbar-thumb-[#5A5A5A] dark:scrollbar-track-transparent`;
}
